import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Team Matrix Widget
 * Grid showing all team performance metrics
 * Displays team standings, driver lineups, and key statistics
 */
const F1TeamMatrix = ({ season = 2025 }) => {
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  // Fetch teams and standings
  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch constructor standings
        const standingsResponse = await f1Service.getConstructorStandings(season);
        if (standingsResponse && standingsResponse.response) {
          setStandings(standingsResponse.response);
        }

        // Fetch all teams
        const teamsResponse = await f1Service.getAllTeams();
        if (teamsResponse && teamsResponse.response) {
          // Filter teams with 2025 season data
          const currentSeasonTeams = teamsResponse.response.filter(team => {
            // Check if team has a logo (indicator of being an active team)
            return team.logo || team.name;
          });
          setTeams(currentSeasonTeams);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching team data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [season]);

  // Get team color
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Get team code
  const getTeamCode = (teamName) => {
    return f1Service.getTeamCode(teamName);
  };

  // Find standing for team
  const getTeamStanding = (teamName) => {
    return standings.find(s => s.team?.name === teamName);
  };

  // Calculate points per race
  const getPointsPerRace = (points, races = 1) => {
    if (!points || !races || races === 0) return 0;
    return (points / races).toFixed(1);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-40 bg-white/10 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (standings.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Team Data Available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Team Performance Matrix
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            {season}
          </span>
        </div>
      </div>

      {/* Team Grid */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {standings.map((standing, index) => {
          const teamColor = getTeamColor(standing.team?.name);
          const teamCode = getTeamCode(standing.team?.name);
          const isLeader = standing.position === 1;
          const isPodium = standing.position <= 3;

          return (
            <motion.div
              key={standing.team?.id || index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.03, duration: 0.2 }}
              className="group cursor-pointer"
            >
              {/* Team Card */}
              <div
                className="h-full border rounded-sm transition-all duration-200 hover:border-opacity-100"
                style={{
                  backgroundColor: `${teamColor}08`,
                  borderColor: `${teamColor}40`
                }}
              >
                {/* Card Header */}
                <div
                  className="p-3 border-b"
                  style={{ borderColor: `${teamColor}20` }}
                >
                  <div className="flex items-start justify-between mb-2">
                    {/* Position Badge */}
                    <div
                      className="px-2 py-1 rounded text-xs font-bold font-mono"
                      style={{
                        backgroundColor: isLeader ? '#E10600' : isPodium ? `${teamColor}40` : '#ffffff10',
                        color: isLeader ? '#ffffff' : isPodium ? '#ffffff' : '#999999'
                      }}
                    >
                      P{standing.position}
                    </div>

                    {/* Team Logo (if available) */}
                    {standing.team?.logo && (
                      <img
                        src={standing.team.logo}
                        alt={standing.team.name}
                        className="h-6 w-auto opacity-70 group-hover:opacity-100 transition-opacity"
                      />
                    )}
                  </div>

                  {/* Team Name */}
                  <div className="space-y-1">
                    <div
                      className="text-xs font-bold uppercase tracking-wider"
                      style={{ color: teamColor }}
                    >
                      {teamCode}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono truncate">
                      {standing.team?.name}
                    </div>
                  </div>
                </div>

                {/* Card Body - Stats */}
                <div className="p-3 space-y-3">
                  {/* Points */}
                  <div className="flex items-end justify-between">
                    <span className="text-[10px] text-gray-600 uppercase tracking-wider font-mono">
                      Points
                    </span>
                    <div className="text-right">
                      <div className="text-lg font-bold text-white tabular-nums">
                        {standing.points || 0}
                      </div>
                    </div>
                  </div>

                  {/* Gap to Leader */}
                  {!isLeader && standing.behind != null && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 font-mono">Gap</span>
                      <span className="text-gray-500 font-mono tabular-nums">
                        -{standing.behind}
                      </span>
                    </div>
                  )}

                  {/* Leader Badge */}
                  {isLeader && (
                    <div className="flex items-center justify-center py-1">
                      <div className="px-2 py-0.5 bg-[#E10600] rounded text-[9px] font-bold text-white uppercase tracking-wider">
                        Championship Leader
                      </div>
                    </div>
                  )}

                  {/* Additional Stats */}
                  <div className="pt-2 border-t border-white/5 space-y-2">
                    {/* Podiums (if available) */}
                    {standing.podiums != null && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-600 font-mono">Podiums</span>
                        <span className="text-white font-bold tabular-nums">
                          {standing.podiums}
                        </span>
                      </div>
                    )}

                    {/* Wins (if available) */}
                    {standing.wins != null && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-600 font-mono">Wins</span>
                        <span className="text-white font-bold tabular-nums">
                          {standing.wins}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer - Team Color Bar */}
                <div
                  className="h-1 transition-all duration-200 group-hover:h-2"
                  style={{ backgroundColor: teamColor }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="p-4 border-t border-white/5 bg-white/[0.01]">
        <div className="grid grid-cols-3 gap-4 text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <div className="flex items-center justify-between">
            <span>Teams</span>
            <span className="text-white font-bold">{standings.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Total Points</span>
            <span className="text-white font-bold">
              {standings.reduce((sum, s) => sum + (s.points || 0), 0)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>Season</span>
            <span className="text-white font-bold">{season}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default F1TeamMatrix;
